import React, { useState, useEffect, useMemo } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  getDoc,
  doc,
  addDoc,
  deleteDoc,
  Timestamp,
} from "firebase/firestore";
import FavouriteButton from "./FavouriteButton";

const WarehouseMultiBookOut = () => {
  const [cylinders, setCylinders] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const firestore = getFirestore();
    const cylindersCollection = collection(firestore, "cylinders_warehouse");
    const snapshot = await getDocs(cylindersCollection);

    // Base rows from warehouse
    const base = snapshot.docs.map((docSnap) => {
      const d = docSnap.data();
      return {
        id: docSnap.id,
        ProductId: d.ProductId || "",          // doc id in cyl_info
        SerialNumber: d.SerialNumber || "",
        LocationId: d.LocationId || "",
        BookedInTime: d.BookedInTime || null,
        BarcodeCombined: `${d.SerialNumber || ""}${d.Special || ""}${d.ProductId || ""}`,
        Stock: d.Stock ? "STOCK" : "ORDER",
        Allocated: d.Allocated || false,
      };
    });

    // Unique ProductIds to look up
    const uniqueIds = Array.from(new Set(base.map(b => b.ProductId).filter(Boolean)));

    // Fetch productCode for each ProductId (doc id in cyl_info)
    const codeEntries = await Promise.all(
      uniqueIds.map(async (pid) => {
        try {
          const infoSnap = await getDoc(doc(firestore, "cyl_info", pid));
          if (!infoSnap.exists()) return [pid, "Not Found"];
          const info = infoSnap.data();
          return [pid, info.productCode || "Unknown"];
        } catch {
          return [pid, "Unknown"];
        }
      })
    );
    const codeMap = Object.fromEntries(codeEntries);

    // Attach ProductCode to each row
    const withCodes = base.map((row) => ({
      ...row,
      ProductCode: row.ProductId ? (codeMap[row.ProductId] || "Unknown") : "Unknown",
    }));

    setCylinders(withCodes);
  };

  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const updated = new Set(prev);
      updated.has(id) ? updated.delete(id) : updated.add(id);
      return updated;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filteredCylinders.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredCylinders.map((c) => c.id)));
    }
  };

  const handleBatchBookOut = async () => {
    if (selectedIds.size === 0) return;
    setLoading(true);
    setStatus("");

    const firestore = getFirestore();
    const archiveRef = collection(firestore, "warehouse_archive");

    let count = 0;

    for (const id of selectedIds) {
      const cylRef = doc(firestore, "cylinders_warehouse", id);
      const snap = await getDoc(cylRef);
      if (snap.exists()) {
        const data = snap.data();
        await addDoc(archiveRef, {
          ...data,
          BookedOutTime: Timestamp.now(),
        });
        await deleteDoc(cylRef);
        count++;
      }
    }

    setStatus(`✅ Successfully booked out ${count} cylinders`);
    setSelectedIds(new Set());
    await fetchData();
    setLoading(false);
  };

  const formatDate = (timestamp) =>
    timestamp?.seconds ? new Date(timestamp.seconds * 1000).toLocaleString() : "N/A";

  const filteredCylinders = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return cylinders.filter((c) =>
      (c.ProductCode && c.ProductCode.toLowerCase().includes(q)) ||
      (c.ProductId && String(c.ProductId).toLowerCase().includes(q)) || // keep searching by raw id if you want
      (c.SerialNumber && c.SerialNumber.toLowerCase().includes(q)) ||
      (c.LocationId && c.LocationId.toLowerCase().includes(q))
    );
  }, [searchQuery, cylinders]);

  return (
    <>
      <FavouriteButton pageUrl={window.location.pathname} />
      <div className="p-6 max-w-7xl mx-auto">
        <h1 className="text-3xl font-semibold mb-4">Multi Book-Out</h1>

        {/* Controls */}
        <div className="flex flex-col md:flex-row items-start md:items-center mb-4 gap-4">
          <input
            type="text"
            placeholder="Search by Serial, Product Code/ID, or Location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full md:w-1/2 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
          <button
            onClick={handleBatchBookOut}
            disabled={selectedIds.size === 0 || loading}
            className={`px-4 py-2 rounded-md text-white ${
              selectedIds.size === 0 || loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loading ? "Booking Out..." : `Book Out Selected (${selectedIds.size})`}
          </button>
        </div>

        {status && <div className="mb-4 text-green-600">{status}</div>}

        {/* Table */}
        <div className="overflow-x-auto border border-gray-200 rounded-md">
          <table className="min-w-full bg-white text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2">
                  <input
                    type="checkbox"
                    onChange={toggleAll}
                    checked={
                      filteredCylinders.length > 0 &&
                      selectedIds.size === filteredCylinders.length
                    }
                  />
                </th>
                <th className="px-4 py-2 text-left">Serial Number</th>
                <th className="px-4 py-2 text-left">Product Code</th>{/* changed */}
                <th className="px-4 py-2 text-left">Location</th>
                <th className="px-4 py-2 text-left">Stock</th>
                <th className="px-4 py-2 text-left">Allocated</th>
                <th className="px-4 py-2 text-left">Booked In</th>
              </tr>
            </thead>
            <tbody>
              {filteredCylinders.map((cyl) => (
                <tr
                  key={cyl.id}
                  className="hover:bg-gray-50 border-t border-gray-100"
                >
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(cyl.id)}
                      onChange={() => toggleSelection(cyl.id)}
                    />
                  </td>
                  <td className="px-4 py-2">{cyl.SerialNumber}</td>
                  <td className="px-4 py-2">{cyl.ProductCode}</td>{/* changed */}
                  <td className="px-4 py-2">{cyl.LocationId}</td>
                  <td className="px-4 py-2">{cyl.Stock}</td>
                  <td className="px-4 py-2">{cyl.Allocated ? "Yes" : "No"}</td>
                  <td className="px-4 py-2">{formatDate(cyl.BookedInTime)}</td>
                </tr>
              ))}
              {filteredCylinders.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center py-4 text-gray-500">
                    No cylinders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default WarehouseMultiBookOut;
